import { EXCLUDED_FLAGS_VARIANT_PARENT, PLACEHOLDER } from "theme/config/constants";

import AspectWrapper from "core-web/components/AspectWrapper";
import Events from "core-web/libs/Events";
import Flags from "theme/components/products/Flags";
import Image from "core-web/components/Image";
import Link from "core-web/components/Link";
import Price from "theme/components/products/Price";
import { ProductEvents } from "core-web/libs/Events/constants";
import PropTypes from "prop-types";
import React from "react";
import TextStyles from "theme/components/text/TextStyles";
import Waterfall from "theme/layouts/Waterfall";
import discountInPercentage from "core-web/utils/discountInPercentage";
import { getPriceColor } from "core-web/utils/getPriceColor";
import getPricelistCodePrefix from "core-web/utils/getPricelistCodePrefix";
import hideOverlays from "core-web/state/models/Overlay/dispatches/hideOverlays";
import { mediaQueryProp } from "core-web/utils/proptypes/modules/mediaProps";
import styled from "theme/libs/styled";
import transformColor from "theme/libs/wordpress/utils/transformColor";
import { useSelector } from "react-redux";
import { useTheme } from "emotion-theming";
import { useTranslation } from "react-i18next";
import { checkForLocalePrefix } from "core-web/utils/checkForLocalePrefix";

const ProductCardWrapper = styled("div")`
    ${({ theme }) => theme.above.tabletLg} {
        img.product-image {
            backface-visibility: visible;
            transform: scale(0.95);
            perspective: 1000px;
            transition: transform ${({ theme }) => theme.transitions.primary};
        }

        .product-name {
            color: ${({ theme }) => theme.colors.black_70};
            transition: color ${({ theme }) => theme.transitions.primary};
        }

        :hover {
            img.product-image {
                transform: scale(1);
            }
            .product-name {
                color: ${({ theme }) => theme.colors.black};
            }
        }
    }
`;

const LinkWrapper = styled(Link)`
    display: flex;
    flex-direction: column;
    gap: 12px;

    ${({ theme }) => theme.above.tabletLg} {
        gap: 20px;
    }
`;

const StyledAspectWrapper = styled(AspectWrapper)`
    position: relative;
    display: grid;
    overflow: hidden;
    background-color: ${({ theme }) => theme.colors.neutral_50};
    padding: 8px;
    border-radius: 8px;

    ${({ theme }) => theme.above.tabletLg} {
        border-radius: 12px;
    }
`;

const ImageWrapper = styled("div")`
    height: 100%;
    width: 100%;
    mix-blend-mode: multiply;
`;

const FlagsWrapper = styled("div")`
    position: absolute;
    inset: 12px 12px auto 12px;
    z-index: 1;
`;

const ProductCard = ({
    name,
    categories,
    priceExVat,
    standardPriceExVat,
    fromExVat,
    standardFromExVat,
    vatRate,
    loading,
    partNo,
    flags,
    url,
    image,
    parentImage,
    isVariant,
    mediaQuery,
    priceListCode,
    priceListId,
    manufacturerName,
    ...rest
}) => {
    const { t } = useTranslation();
    const { colors } = useTheme();
    const { badges, price_colors: priceColorFlags } =
        useSelector(({ application }) => application.config.options.products) || {};
    const { code: defaultPriceListCode } = useSelector(({ application }) => application?.defaultPriceList) || {};

    const ecommerceEnabled = useSelector(({ application }) => application.ecommerceEnabled);
    const savedAutomatedDiscounts = useSelector(({ products }) => products.automatedDiscounts);

    const enabledFlags = badges?.enabled_flags || [];
    const pricelistPrefix = getPricelistCodePrefix(priceListCode);
    const isCompetitorPricelist = pricelistPrefix === "competitor";

    const handleGoToProduct = () => {
        hideOverlays();
        Events.trigger(ProductEvents.CLICK, {
            product: {
                name,
                priceExVat,
                vatRate,
                categories,
                partNo,
                manufacturerName,
            },
        });
    };

    let hasAutomatedDiscountPrice = false;
    let automatedDiscountPrice = null;

    if (Object.keys(savedAutomatedDiscounts).some((automatedDiscountPartNo) => automatedDiscountPartNo === partNo)) {
        hasAutomatedDiscountPrice = true;
        automatedDiscountPrice = savedAutomatedDiscounts[partNo].discountPriceExVat;
    }

    const discountPercentage =
        isVariant && fromExVat
            ? discountInPercentage(standardFromExVat, fromExVat)
            : discountInPercentage(standardPriceExVat, priceExVat);

    let showDiscount = discountPercentage > 9.5 && !isCompetitorPricelist && !hasAutomatedDiscountPrice;
    if (priceListCode && showDiscount) {
        showDiscount = priceListCode !== defaultPriceListCode;
    }

    const filteredEnabledFlags =
        enabledFlags &&
        (isVariant
            ? enabledFlags.filter(
                  (enabledFlag) => enabledFlag?.flag && !EXCLUDED_FLAGS_VARIANT_PARENT.includes(enabledFlag.flag.code),
              )
            : enabledFlags);

    const filteredFlags = filteredEnabledFlags
        ? filteredEnabledFlags.filter((filteredEnabledFlags) =>
              flags?.some((flag) => flag?.id === filteredEnabledFlags?.flag?.id),
          )
        : [];

    const priceColor = getPriceColor(priceColorFlags, flags);
    const transformedPriceColor = transformColor(priceColor);

    // A card with no url can't be linked (the whole card is a Link) and url.replace() below would
    // throw — which the page-level ErrorCatcher turns into a blank "Something went wrong..." view.
    if (typeof url !== "string" || url.trim() === "") {
        return null;
    }

    const localPrefix = checkForLocalePrefix(url);
    const productSlug = url.replace(`${localPrefix}/`, "");

    return (
        <ProductCardWrapper as="article" className="product-card" {...rest}>
            <LinkWrapper to={productSlug} onClick={handleGoToProduct}>
                <StyledAspectWrapper ratio={3 / 4}>
                    {!!filteredFlags?.length && (
                        <FlagsWrapper>
                            <Flags flags={filteredFlags} />
                        </FlagsWrapper>
                    )}
                    <ImageWrapper p={["24px", null, null, null, "48px 44px"]}>
                        <Image
                            loading={loading}
                            alt={name}
                            title={name}
                            className="product-image"
                            lazyLoadWrapperStyling={{
                                top: ["24px", null, null, null, "40px"],
                                bottom: ["24px", null, null, null, "40px"],
                                right: ["24px", null, null, null, "40px"],
                                left: ["24px", null, null, null, "40px"],
                            }}
                            src={{
                                url: image || parentImage || PLACEHOLDER,
                                width: mediaQuery?.w || [140, 200, 280, 250, 300, 350, 400],
                                q: mediaQuery?.q || 70,
                                f: "webp",
                            }}
                            sizes={["90px", "150px", null, "250px", "350px"]}
                            pointerEvents="none"
                        />
                    </ImageWrapper>
                </StyledAspectWrapper>

                <Waterfall rowGap={["12px", null, null, null, "8px"]}>
                    <TextStyles
                        className="product-name"
                        textStyles={["Myriad Pro/16-140", null, null, null, "Myriad Pro/18-140"]}
                    >
                        {name}
                    </TextStyles>
                    <TextStyles
                        textStyles="Myriad Pro/14-100"
                        color={colors.black_70}
                        mb={ecommerceEnabled ? [null, null, null, null, "8px"] : null}
                    >
                        {t(`views.product.product_information.partNos`)}: {partNo}
                    </TextStyles>
                    {ecommerceEnabled && (
                        <>
                            {hasAutomatedDiscountPrice ? (
                                <Price
                                    skipDiscountCheck
                                    priceExVat={automatedDiscountPrice}
                                    standardPriceExVat={standardPriceExVat}
                                    vatRate={vatRate}
                                    priceListCode={priceListCode}
                                    priceListId={priceListId}
                                    color={colors[transformedPriceColor]}
                                    isCompetitorPricelist={isCompetitorPricelist}
                                />
                            ) : isVariant && fromExVat ? (
                                <Price
                                    showFromPrice
                                    priceExVat={fromExVat}
                                    standardPriceExVat={standardFromExVat}
                                    vatRate={vatRate}
                                    priceListCode={priceListCode}
                                    priceListId={priceListId}
                                    color={colors[transformedPriceColor]}
                                    isCompetitorPricelist={isCompetitorPricelist}
                                />
                            ) : (
                                <Price
                                    priceExVat={priceExVat}
                                    standardPriceExVat={standardPriceExVat}
                                    vatRate={vatRate}
                                    priceListCode={priceListCode}
                                    priceListId={priceListId}
                                    color={colors[transformedPriceColor]}
                                    isCompetitorPricelist={isCompetitorPricelist}
                                />
                            )}
                        </>
                    )}
                </Waterfall>
            </LinkWrapper>
        </ProductCardWrapper>
    );
};

ProductCard.propTypes = {
    categories: PropTypes.array,
    categoryPrimary: PropTypes.number,
    flags: PropTypes.array,
    fromExVat: PropTypes.number,
    image: PropTypes.string.isRequired,
    inStock: PropTypes.bool.isRequired,
    isPackage: PropTypes.bool.isRequired,
    isRecommendedCard: PropTypes.bool,
    isVariant: PropTypes.bool,
    loading: PropTypes.string,
    manufacturerName: PropTypes.string,
    mediaQuery: mediaQueryProp,
    name: PropTypes.string.isRequired,
    parametrics: PropTypes.object.isRequired,
    parentImage: PropTypes.string,
    partNo: PropTypes.string.isRequired,
    presetPartNo: PropTypes.string,
    priceExVat: PropTypes.number.isRequired,
    priceListCode: PropTypes.string,
    priceListId: PropTypes.number,
    productId: PropTypes.number.isRequired,
    recommendationId: PropTypes.string,
    standardFromExVat: PropTypes.number,
    standardPriceExVat: PropTypes.number,
    stockStatus: PropTypes.shape({
        stock: PropTypes.number,
        leadTime: PropTypes.number,
        nextDelivery: PropTypes.string,
    }).isRequired,
    url: PropTypes.string.isRequired,
    userId: PropTypes.string,
    vatRate: PropTypes.number,
};

ProductCard.defaultProps = {
    categories: [],
    categoryPrimary: null,
    flags: [],
    fromExVat: null,
    isRecommendedCard: false,
    isVariant: false,
    loading: "lazy",
    manufacturerName: null,
    mediaQuery: {
        w: [140, 200, 280, 250, 300, 350, 400],
    },
    parentImage: null,
    presetPartNo: null,
    priceListCode: null,
    priceListId: null,
    recommendationId: null,
    standardFromExVat: null,
    standardPriceExVat: null,
    userId: null,
    vatRate: 1,
};

export default ProductCard;
